import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2, Upload, FileText, X, HelpCircle } from "lucide-react";
import { z } from "zod";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { signUp } from "@/utils/authService";
import { validateQualificationFile, uploadQualificationDocument } from "@/utils/qualificationDocuments";
import { useIsMobile } from "@/hooks/use-mobile";

// Form validation schema
const signupSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  verifyPassword: z.string().min(1, "Please verify your password"),
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  profileType: z.string().min(1, "Please select a profile type"),
}).refine((data) => data.password === data.verifyPassword, {
  message: "Passwords don't match",
  path: ["verifyPassword"],
});

const SignUpForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // User data state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileType, setProfileType] = useState<string>("");
  const [profession, setProfession] = useState("");
  const [location, setLocation] = useState("");
  const [education, setEducation] = useState("");
  const [qualificationDocuments, setQualificationDocuments] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Mobile tooltip state
  const [mobileTooltips, setMobileTooltips] = useState({
    profileType: false,
    qualificationDocs: false
  });

  const toggleMobileTooltip = (tooltipType: keyof typeof mobileTooltips) => {
    if (isMobile) {
      setMobileTooltips(prev => ({
        ...prev,
        [tooltipType]: !prev[tooltipType]
      }));
    }
  };

  const handleFileUpload = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const fileErrors: string[] = [];

    fileArray.forEach(file => {
      const validation = validateQualificationFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        fileErrors.push(`${file.name}: ${validation.error}`);
      }
    });

    if (validFiles.length > 0) {
      setQualificationDocuments(prev => [...prev, ...validFiles]);
    }
    
    if (fileErrors.length > 0) {
      setErrors(prev => ({ ...prev, file: fileErrors.join(", ") }));
    } else {
      setErrors(prev => ({ ...prev, file: "" }));
    }
  };

  const handleInputFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      handleFileUpload(event.target.files);
    }
  };

  const handleRemoveFile = (index: number) => {
    setQualificationDocuments(prev => prev.filter((_, i) => i !== index));
    setErrors(prev => ({ ...prev, file: "" }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    try {
      signupSchema.parse({
        email,
        password,
        verifyPassword,
        firstName,
        lastName,
        profileType
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          if (err.path) {
            newErrors[err.path[0]] = err.message;
          }
        });
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
    // Upload qualification documents to staging if present
    if (qualificationDocuments.length > 0) {
      for (const document of qualificationDocuments) {
        try {
          // Upload to staging area in storage
          const fileExt = document.name.split('.').pop() || 'bin';
          const stagingPath = `staging/${email}/${Date.now()}-${document.name}`;
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('qualification-documents')
            .upload(stagingPath, document, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error("Upload failed:", uploadError);
            setErrors(prev => ({ ...prev, file: `Failed to upload ${document.name}` }));
            setLoading(false);
            return;
          }

          // Insert metadata into staging table
          const { error: stagingError } = await supabase
            .from('qualification_documents_staging')
            .insert({
              user_email: email,
              file_name: document.name,
              file_path: stagingPath,
              file_type: document.type,
              file_size: document.size
            });

          if (stagingError) {
            console.error("Failed to save document metadata:", stagingError);
            setErrors(prev => ({ ...prev, file: `Failed to process ${document.name}` }));
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Failed to process document:', error);
          setErrors(prev => ({ ...prev, file: `Failed to process ${document.name}` }));
          setLoading(false);
          return;
        }
      }
    }

      // Create the new user - no qualification documents in metadata since they're staged
      const result = await signUp(email, password, {
        email,
        firstName,
        lastName,
        profileType,
        profession,
        location,
        education
      });
      
      if (result.success) {
        toast({
          title: "Account created successfully!",
          description: "Please check your email to verify your account. After verification, your account will be reviewed by an administrator.",
        });
        
        // Redirect to login page
        navigate("/login");
      } else {
        throw new Error(result.message || "Failed to create account");
      }
    } catch (error: any) {
      console.error("Signup error:", error);
      toast({
        title: "Signup Failed",
        description: error.message || "An error occurred during signup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <form onSubmit={handleSubmit} className="space-y-4">
      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={errors.email ? "border-red-500" : ""}
          placeholder="example@email.com"
        />
        {errors.email && (
          <p className="text-xs text-red-500">{errors.email}</p>
        )}
      </div>
      
      {/* Password */}
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={errors.password ? "border-red-500 pr-10" : "pr-10"}
            placeholder="Enter at least 8 characters"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-500"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-red-500">{errors.password}</p>
        )}
      </div>
      
      {/* Verify Password */}
      <div className="space-y-2">
        <Label htmlFor="verifyPassword">Verify Password</Label>
        <div className="relative">
          <Input
            id="verifyPassword"
            type={showVerifyPassword ? "text" : "password"}
            value={verifyPassword}
            onChange={(e) => setVerifyPassword(e.target.value)}
            className={errors.verifyPassword ? "border-red-500 pr-10" : "pr-10"}
            placeholder="Re-enter your password"
          />
          <button
            type="button"
            onClick={() => setShowVerifyPassword(!showVerifyPassword)}
            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-500"
          >
            {showVerifyPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.verifyPassword && (
          <p className="text-xs text-red-500">{errors.verifyPassword}</p>
        )}
      </div>
      {/* First Name */}
      <div className="space-y-2">
        <Label htmlFor="firstName">First Name</Label>
        <Input
          id="firstName"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className={errors.firstName ? "border-red-500" : ""}
          placeholder="John"
        />
        {errors.firstName && (
          <p className="text-xs text-red-500">{errors.firstName}</p>
        )}
      </div>
      
      {/* Last Name */}
      <div className="space-y-2">
        <Label htmlFor="lastName">Last Name</Label>
        <Input
          id="lastName"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          className={errors.lastName ? "border-red-500" : ""}
          placeholder="Doe"
        />
        {errors.lastName && (
          <p className="text-xs text-red-500">{errors.lastName}</p>
        )}
      </div>
      
      {/* Profile Type */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="profileType">Profile Type</Label>
          <Tooltip open={isMobile ? mobileTooltips.profileType : undefined}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={`${isMobile ? 'p-2 -m-2' : ''} cursor-help`}
                onClick={() => toggleMobileTooltip('profileType')}
              >
                <HelpCircle className={`${isMobile ? 'h-5 w-5' : 'h-4 w-4'} text-gray-400 hover:text-gray-600`} />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <div className="space-y-2">
                <p><strong>Mentee</strong> – You're looking for guidance, support, or industry insight from experienced professionals.</p>
                <p><strong>Mentor</strong> – You're an experienced professional willing to guide and support others in their growth.</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
        <Select value={profileType} onValueChange={setProfileType}>
          <SelectTrigger className={errors.profileType ? "border-red-500" : ""}>
            <SelectValue placeholder="Select profile type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mentee">Mentee</SelectItem>
            <SelectItem value="mentor">Mentor</SelectItem>
          </SelectContent>
        </Select>
        {errors.profileType && (
          <p className="text-xs text-red-500">{errors.profileType}</p>
        )}
      </div>
      
      {/* Profession */}
      <div className="space-y-2">
        <Label htmlFor="profession">Profession</Label>
        <Input
          id="profession"
          value={profession}
          onChange={(e) => setProfession(e.target.value)}
          placeholder="Mechanical Engineer"
        />
      </div>
      
      {/* Location */}
      <div className="space-y-2">
        <Label htmlFor="location">Location</Label>
        <Input
          id="location"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Brisbane, Australia"
        />
      </div>
      
      {/* Education */}
      <div className="space-y-2">
        <Label htmlFor="education">Education</Label>
        <Input
          id="education"
          value={education}
          onChange={(e) => setEducation(e.target.value)}
          placeholder="Bachelor of Mechanical Engineering"
        />
      </div>

      {/* File Upload Section */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="qualification_document" className="text-sm font-medium">
            Qualification Documents
          </Label>
          <Tooltip open={isMobile ? mobileTooltips.qualificationDocs : undefined}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={`${isMobile ? 'p-2 -m-2' : ''} cursor-help`}
                onClick={() => toggleMobileTooltip('qualificationDocs')}
              >
                <HelpCircle className={`${isMobile ? 'h-5 w-5' : 'h-4 w-4'} text-gray-400 hover:text-gray-600`} />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-md">
              <div className="space-y-3">
                <p><strong>Qualification Documents (Required)</strong></p>
                <p>Please upload your university degree or official transcript as proof of eligibility to join the platform.</p>
                <div>
                  <p><strong>Mentees:</strong></p>
                  <p>• Must provide evidence of current or completed tertiary education (e.g. undergraduate degree, transcript, or enrolment letter).</p>
                </div>
                <div>
                  <p><strong>Mentors:</strong></p>
                  <p>• Must upload proof of a completed university degree (Bachelor's or higher) in a relevant field.</p>
                  <p>• Additional certifications (e.g. industry credentials, teaching experience) are encouraged.</p>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
        <div 
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging 
              ? 'border-emu-blue bg-emu-blue/5' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="qualification_document"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleInputFileUpload}
            className="hidden"
            multiple
          />
          <label
            htmlFor="qualification_document"
            className="cursor-pointer flex flex-col items-center justify-center"
          >
            <Upload className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-600">
              Click to upload or drag and drop
            </span>
            <span className="text-xs text-gray-500 mt-1">
              PDF, JPG, JPEG, PNG (max 10MB each)
            </span>
          </label>
        </div>
        
        {/* Display uploaded files */}
        {qualificationDocuments.length > 0 && (
          <div className="space-y-2">
            {qualificationDocuments.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{file.name}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFile(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        
        {errors.file && (
          <p className="text-sm text-red-500">{errors.file}</p>
        )}
      </div>
      
      {/* Terms and conditions */}
      <div className="text-sm text-gray-600 mt-4">
        By creating an account, you agree to the{" "}
        <Link to="/terms" className="text-emu-blue hover:underline">
          Terms of Service
        </Link>
        {" "}and{" "}
        <Link to="/privacy" className="text-emu-blue hover:underline">
          Privacy Policy
        </Link>
        .
        {profileType === "mentor" && (
          <>
            <br />
            If you register as a Mentor, you also agree to the{" "}
            <Link to="/mentor-agreement" className="text-emu-blue hover:underline">
              Mentor Agreement
            </Link>
            .
          </>
        )}
      </div>
      
      {/* Submit button */}
      <Button 
        type="submit" 
        className="w-full bg-emu-blue hover:bg-blue-700"
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Creating Account...
          </>
        ) : (
          "Create Account"
        )}
      </Button>
      </form>
    </TooltipProvider>
  );
};

export default SignUpForm;